import { Button, Heading, Input, Separator, Text, toast } from "@overmap-ai/blocks"
import { generateAbbreviation } from "@overmap-ai/common"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { type FormEventHandler, memo, useCallback, useState } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { useChangeEventHandler } from "../../hooks/utils"
import { sdk } from "../../sdk"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/forms/create")

export const CreateFormView = memo(() => {
	const { organizationId } = routeApi.useParams()

	const navigate = useNavigate()
	const [name, setName] = useState<string>("")

	const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
		(e) => {
			e.preventDefault()
			e.stopPropagation()

			const trimmedName = name.trim()

			if (!trimmedName) {
				toast.error({
					title: "Name is required",
					description: "Please enter a name for the form.",
				})
				return
			}

			const [offlineForm] = sdk.forms.add({
				organization: organizationId,
				name: trimmedName,
				abbr: generateAbbreviation(trimmedName),
				description: null,
			})

			setName("")

			void navigate({
				from: "/$organizationId/settings/forms/create",
				to: "/$organizationId/settings/forms/$formId",
				params: (prev) => ({
					...prev,
					formId: offlineForm.uuid,
				}),
			})
		},
		[name, navigate, organizationId],
	)

	const handleNameChange = useChangeEventHandler(setName)

	return (
		<ViewContainer maxWidth="2xl">
			<section className="flex h-max w-full flex-col gap-4">
				<div className="flex h-max w-full flex-col gap-1">
					<Heading size="xl">Create form</Heading>
					<Text size="sm" accentColor="base">
						Create a new form to capture customized information in your organization.
					</Text>
				</div>

				<Separator size="full" />

				<form className="w-full" onSubmit={handleSubmit}>
					<div className="flex w-full flex-col gap-4">
						<Input.Root variant="outline" className="w-full max-w-[300px]">
							<Input.Field placeholder="Enter a name" value={name} onChange={handleNameChange} />
						</Input.Root>

						<Button className="w-max" type="submit" variant="surface">
							Create
						</Button>
					</div>
				</form>
			</section>
		</ViewContainer>
	)
})
CreateFormView.displayName = "CreateFormView"
